<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import PlayerIconButton from '@/components/quran/player/PlayerIconButton.vue';
import UiButton from '@/components/ui/UiButton.vue';
import { useQuranPlayer } from '@/composables/quran/useQuranPlayer';
import { AYAH_REPEATS, useQuranSettings } from '@/composables/quran/useQuranSettings';

/**
 * Repeat-all · previous · play/pause · next · repeat-each-ayah.
 * The ayah repeat cycles 1× → 3× → 5× → 10× → ∞, the hifz loop.
 */
defineProps({
  size: { type: String, default: 'md' },
});

const { t } = useI18n();
const player = useQuranPlayer();
const settings = useQuranSettings();
const state = player.state;

const ayahRepeatLabel = computed(() => (settings.ayahRepeat === 0 ? '∞' : `${settings.ayahRepeat}×`));

function cycleAyahRepeat() {
  const i = AYAH_REPEATS.indexOf(settings.ayahRepeat);
  settings.ayahRepeat = AYAH_REPEATS[(i + 1) % AYAH_REPEATS.length];
}
</script>

<template>
  <div class="flex items-center justify-center" :class="size === 'lg' ? 'gap-4 sm:gap-6' : 'gap-1.5 sm:gap-3'">
    <PlayerIconButton
      icon="solar:repeat-linear"
      :size="size === 'lg' ? 'md' : 'sm'"
      :label="t('quran.player.repeatAll')"
      :active="settings.repeat === 'all'"
      @click="settings.repeat = settings.repeat === 'all' ? 'off' : 'all'"
    />
    <PlayerIconButton
      icon="solar:skip-previous-bold"
      :size="size === 'lg' ? 'lg' : 'md'"
      :label="t('quran.player.previous')"
      :disabled="!player.hasPrevious.value && state.currentTime < 3"
      @click="player.previous"
    />
    <UiButton
      intent="gold"
      icon-only
      :class="['rounded-full shadow-[0_10px_30px_-8px_rgba(236,215,159,0.6)]', size === 'lg' ? '!w-[76px] !h-[76px] [&>iconify-icon]:!text-4xl' : '!w-12 !h-12 [&>iconify-icon]:!text-2xl']"
      :loading="state.isLoading"
      :icon="state.isPlaying ? 'solar:pause-bold' : 'solar:play-bold'"
      :aria-label="state.isPlaying ? t('quran.player.pause') : t('quran.player.play')"
      @click="player.toggle"
    />
    <PlayerIconButton
      icon="solar:skip-next-bold"
      :size="size === 'lg' ? 'lg' : 'md'"
      :label="t('quran.player.next')"
      :disabled="!player.hasNext.value"
      @click="player.next"
    />
    <PlayerIconButton
      icon="solar:repeat-one-linear"
      :size="size === 'lg' ? 'md' : 'sm'"
      :label="t('quran.player.ayahRepeat', { n: ayahRepeatLabel })"
      :active="settings.ayahRepeat !== 1"
      :badge="settings.ayahRepeat !== 1 ? ayahRepeatLabel : null"
      @click="cycleAyahRepeat"
    />
  </div>
</template>
